import { useState } from 'react';
import { useTheme, THEMES } from '../../context/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { Shield, User, Mail, Check, Eye } from 'lucide-react';

export const SettingsDialog = ({ open, onOpenChange }) => {
  const { theme, setTheme, previewTheme, cancelPreview } = useTheme();
  const { user, userRole } = useAuth();
  const [hoveredTheme, setHoveredTheme] = useState(null);

  const roleLabel = {
    admin: 'Administrator',
    project_manager: 'Project Manager',
    user: 'Viewer',
  };

  // Handle theme hover for live preview
  const handleThemeHover = (themeKey) => {
    setHoveredTheme(themeKey);
    if (previewTheme) {
      previewTheme(themeKey);
    }
  };

  // Handle mouse leave - revert to selected theme
  const handleThemeLeave = () => {
    setHoveredTheme(null);
    if (cancelPreview) {
      cancelPreview();
    }
  };

  // Handle theme selection
  const handleThemeSelect = (themeKey) => {
    setTheme(themeKey, user?.email);
    setHoveredTheme(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Profile Info */}
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Profile</Label>
            <div className="rounded-lg border border-border/50 bg-card/60 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{user?.name || 'User'}</p>
                  <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {user?.email}
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="text-xs gap-1">
                <Shield className="w-3 h-3" />
                {roleLabel[userRole] || 'Viewer'}
              </Badge>
            </div>
          </div>

          {/* Theme Picker with Live Preview */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Theme</Label>
              {hoveredTheme && (
                <Badge variant="outline" className="text-xs gap-1 animate-pulse">
                  <Eye className="w-3 h-3" />
                  Previewing
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(THEMES).map(([key, t]) => (
                <button
                  key={key}
                  onClick={() => handleThemeSelect(key)}
                  onMouseEnter={() => handleThemeHover(key)}
                  onMouseLeave={handleThemeLeave}
                  className={cn(
                    "relative rounded-lg border-2 p-3 text-left transition-all",
                    theme === key
                      ? "border-primary shadow-md shadow-primary/10"
                      : hoveredTheme === key
                      ? "border-primary/60 shadow-sm"
                      : "border-border/50 hover:border-primary/40"
                  )}
                  style={{ backgroundColor: t.preview.bg }}
                  data-testid={`theme-${key}`}
                >
                  {/* Preview */}
                  <div className="space-y-2 mb-2">
                    <div className="flex gap-1.5">
                      <div className="h-2 w-8 rounded-full" style={{ backgroundColor: t.preview.accent }} />
                      <div className="h-2 w-5 rounded-full opacity-40" style={{ backgroundColor: t.preview.text }} />
                    </div>
                    <div className="flex gap-1.5">
                      <div className="h-6 flex-1 rounded" style={{ backgroundColor: t.preview.card }} />
                      <div className="h-6 flex-1 rounded" style={{ backgroundColor: t.preview.card }} />
                    </div>
                  </div>
                  <p className="text-xs font-semibold" style={{ color: t.preview.text }}>{t.label}</p>
                  <p className="text-[10px] opacity-60" style={{ color: t.preview.text }}>{t.description}</p>
                  {theme === key && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                      <Check className="w-3 h-3 text-primary-foreground" />
                    </div>
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Hover over a theme to preview it live
            </p>
          </div>

          {/* About */}
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">About</Label>
            <div className="rounded-lg border border-border/50 bg-card/60 p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Dashboard Version</span>
                <span className="font-medium">2.0</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Platform</span>
                <span className="font-medium">Multi-Tenant</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
